import { ArrowRight, Loader2, Shield, UserCircle } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { onboardingCopy } from "@/lib/onboarding-copy";
import {
  professionalRegistrationSchema,
  healthAreaOnlySchema,
  PROFESSIONAL_ONBOARDING_ACCOUNT_TYPE,
  type ProfessionalRegistrationForm,
  type HealthAreaOnlyForm,
  type ProfessionalSignUpPayload,
} from "@/lib/onboarding/onboarding.schemas";
import type { HealthArea } from "@/hooks/useHealthAreas";
import { useLayoutEffect, useRef, useState } from "react";
import {
  loadTurnstileScript,
  verifyTurnstileToken,
  getTurnstileSiteKey,
  getTurnstileWidgetErrorUserMessage,
} from "@/utils/turnstile";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          size?: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "error-callback"?: (errorCode?: string) => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

interface Step1ProfessionalRegistrationProps {
  isAuthenticated: boolean;
  formData: ProfessionalRegistrationForm;
  onFormChange: (data: Partial<ProfessionalRegistrationForm>) => void;
  healthAreas: HealthArea[];
  onRegister: (data: ProfessionalSignUpPayload) => Promise<void>;
  onSaveHealthArea: (data: HealthAreaOnlyForm) => Promise<void>;
  isRegistering: boolean;
  isSaving: boolean;
  onLoginClick: () => void;
  onBackClick: () => void;
}

export function Step1ProfessionalRegistration({
  isAuthenticated,
  formData,
  onFormChange,
  healthAreas,
  onRegister,
  onSaveHealthArea,
  isRegistering,
  isSaving,
  onLoginClick,
  onBackClick,
}: Step1ProfessionalRegistrationProps) {
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fullForm = useForm<ProfessionalRegistrationForm>({
    resolver: zodResolver(professionalRegistrationSchema),
    defaultValues: formData,
    values: formData,
  });

  const healthAreaForm = useForm<HealthAreaOnlyForm>({
    resolver: zodResolver(healthAreaOnlySchema),
    defaultValues: { healthAreaId: formData.healthAreaId },
    values: { healthAreaId: formData.healthAreaId },
  });

  // Carregar script do Turnstile apenas para o formulário de registro completo
  useLayoutEffect(() => {
    if (isAuthenticated) return; // Não precisa do Turnstile se já está autenticado

    const siteKey = getTurnstileSiteKey();
    console.log(
      "🔍 Turnstile - Site Key:",
      siteKey ? "✅ Configurada" : "❌ Não configurada",
    );

    if (!siteKey) {
      console.warn(
        "⚠️ Turnstile site key não configurada. O widget não será exibido.",
      );
      return;
    }

    const renderWidget = () => {
      if (!turnstileRef.current) {
        console.error("❌ Turnstile - Elemento não encontrado");
        return;
      }

      if (!window.turnstile) {
        console.error("❌ Turnstile - API não disponível");
        return;
      }

      try {
        console.log("🔄 Turnstile - Renderizando widget...");
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          size: "normal",
          theme: "light",
          callback: (token: string) => {
            console.log("✅ Turnstile - Token gerado");
            turnstileTokenRef.current = token;
          },
          "error-callback": (errorCode?: string) => {
            console.error("❌ Turnstile - Erro no widget (error-callback)", errorCode);
            turnstileTokenRef.current = null;
            toast.error(getTurnstileWidgetErrorUserMessage(errorCode));
          },
        });
        turnstileWidgetId.current = widgetId;
        console.log(
          "✅ Turnstile - Widget renderizado com sucesso, ID:",
          widgetId,
        );
      } catch (error) {
        console.error("❌ Turnstile - Erro ao renderizar:", error);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (errorMessage.includes("110200")) {
          toast.error(
            "Erro na configuração do Turnstile. Verifique se o domínio está autorizado no Cloudflare.",
          );
        }
      }
    };

    const scheduleRender = () => {
      renderWidget();
      if (turnstileWidgetId.current) return;
      requestAnimationFrame(() => {
        renderWidget();
        if (turnstileWidgetId.current) return;
        setTimeout(() => {
          console.log("🔄 Turnstile - Tentando renderizar novamente...");
          renderWidget();
        }, 500);
      });
    };

    loadTurnstileScript()
      .then(() => {
        console.log("✅ Turnstile - Script carregado");
        scheduleRender();
      })
      .catch((error) => {
        console.error("❌ Erro ao carregar Turnstile:", error);
      });

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, [isAuthenticated]);

  const handleFullSubmit = fullForm.handleSubmit(async (data) => {
    setIsSubmitting(true);
    // Verificar Turnstile antes de registrar
    const siteKey = getTurnstileSiteKey();
    if (siteKey) {
      if (!turnstileWidgetId.current || !window.turnstile) {
        toast.error(
          "Verificação de segurança não carregada. Recarregue a página.",
        );
        return;
      }

      // Tentar obter token do ref primeiro (mais confiável)
      let turnstileToken = turnstileTokenRef.current;

      // Se não há token no ref, tentar obter do widget
      if (!turnstileToken) {
        turnstileToken = window.turnstile.getResponse(
          turnstileWidgetId.current,
        );
      }

      // Se ainda não há token, resetar e aguardar o usuário completar novamente
      if (!turnstileToken) {
        toast.error("Por favor, complete a verificação de segurança");
        // Resetar widget para gerar novo token
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileTokenRef.current = null;
        return;
      }

      // Verificar token no servidor imediatamente (antes que expire)
      const verificationResult = await verifyTurnstileToken(turnstileToken);
      if (!verificationResult.success) {
        // Se o erro for timeout-or-duplicate, resetar e pedir nova verificação
        if (verificationResult.errorCodes?.includes("timeout-or-duplicate")) {
          toast.error(
            "A verificação expirou. Por favor, complete a verificação novamente.",
          );
          // Resetar widget para gerar novo token
          if (turnstileWidgetId.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
            turnstileTokenRef.current = null;
          }
        } else {
          toast.error(
            verificationResult.error ||
              "Verificação de segurança falhou. Tente novamente.",
          );
          // Resetar widget para permitir nova tentativa
          if (turnstileWidgetId.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
            turnstileTokenRef.current = null;
          }
        }
        return;
      }

      // Limpar token após verificação bem-sucedida (mas ainda não usamos na requisição)
      // Não resetar ainda - vamos resetar apenas se a requisição falhar
    }

    try {
      const payload: ProfessionalSignUpPayload = {
        ...data,
        account_type: PROFESSIONAL_ONBOARDING_ACCOUNT_TYPE,
      };
      await onRegister(payload);
      // Se chegou aqui, a requisição foi bem-sucedida
      // Agora sim, resetar o widget para gerar novo token para próximas submissões
      if (siteKey && turnstileWidgetId.current && window.turnstile) {
        turnstileTokenRef.current = null;
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } catch (error) {
      // Se a requisição falhou, resetar o widget para permitir nova tentativa
      if (siteKey && turnstileWidgetId.current && window.turnstile) {
        turnstileTokenRef.current = null;
        window.turnstile.reset(turnstileWidgetId.current);
      }
      // Re-throw o erro para que o componente pai possa tratá-lo
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleHealthAreaSubmit = healthAreaForm.handleSubmit(async (data) => {
    await onSaveHealthArea(data);
  });

  const isLoading = isRegistering || isSaving || isSubmitting;

  if (isAuthenticated) {
    return (
      <section className="bg-white border-2 rounded-2xl p-8 shadow-2xl space-y-6 animate-fade-in max-w-2xl mx-auto">
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mb-4">
            <UserCircle className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold">Selecione sua área de atuação</h2>
          <p className="text-muted-foreground">
            Para personalizar sua experiência, selecione sua especialidade na
            saúde.
          </p>
        </div>

        <form onSubmit={handleHealthAreaSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="healthArea">
              {onboardingCopy.register.fields.healthArea.label}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Select
              key={`health-area-select-${healthAreas.length}-${formData.healthAreaId}`}
              value={formData.healthAreaId}
              onValueChange={(v) => onFormChange({ healthAreaId: v })}
            >
              <SelectTrigger id="healthArea">
                <SelectValue
                  placeholder={
                    onboardingCopy.register.fields.healthArea.placeholder
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {healthAreas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-end pt-4">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading || !formData.healthAreaId}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {onboardingCopy.general.saving}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Continuar
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </Button>
          </div>
        </form>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
          <Shield className="h-4 w-4" />
          <span>{onboardingCopy.general.lgpd}</span>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white border-2 rounded-2xl p-8 shadow-2xl space-y-6 animate-fade-in max-w-2xl mx-auto">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mb-4">
          <UserCircle className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold">{onboardingCopy.register.title}</h2>
        <p className="text-muted-foreground">
          Crie sua conta para começar a usar a plataforma
        </p>
      </div>

      <form onSubmit={handleFullSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">
            {onboardingCopy.register.fields.name.label}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            placeholder={onboardingCopy.register.fields.name.placeholder}
            {...fullForm.register("name", {
              onChange: (e) => onFormChange({ name: e.target.value }),
            })}
            className={
              fullForm.formState.errors.name ? "border-destructive" : ""
            }
          />
          {fullForm.formState.errors.name && (
            <p className="text-sm text-destructive">
              {fullForm.formState.errors.name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">
            {onboardingCopy.register.fields.email.label}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder={onboardingCopy.register.fields.email.placeholder}
            {...fullForm.register("email", {
              onChange: (e) => onFormChange({ email: e.target.value }),
            })}
          />
          {fullForm.formState.errors.email && (
            <p className="text-sm text-destructive">
              {fullForm.formState.errors.email.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">
            {onboardingCopy.register.fields.password.label}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder={onboardingCopy.register.fields.password.placeholder}
            {...fullForm.register("password", {
              onChange: (e) => onFormChange({ password: e.target.value }),
            })}
          />
          {fullForm.formState.errors.password && (
            <p className="text-sm text-destructive">
              {fullForm.formState.errors.password.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="healthArea">
            {onboardingCopy.register.fields.healthArea.label}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <Select
            key={`health-area-select-full-${healthAreas.length}-${formData.healthAreaId}`}
            value={formData.healthAreaId}
            onValueChange={(v) => onFormChange({ healthAreaId: v })}
          >
            <SelectTrigger id="healthArea">
              <SelectValue
                placeholder={
                  onboardingCopy.register.fields.healthArea.placeholder
                }
              />
            </SelectTrigger>
            <SelectContent>
              {healthAreas.map((area) => (
                <SelectItem key={area.id} value={area.id}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fullForm.formState.errors.healthAreaId && (
            <p className="text-sm text-destructive">
              {fullForm.formState.errors.healthAreaId.message}
            </p>
          )}
        </div>

        {getTurnstileSiteKey() ? (
          <div className="flex flex-col items-center justify-center py-2 min-h-[65px]">
            <div
              ref={turnstileRef}
              id="turnstile-widget-register"
              className="w-full flex justify-center"
              style={{ minHeight: "65px" }}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-end pt-4">
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                {onboardingCopy.general.saving}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Criar conta
                <ArrowRight className="h-5 w-5" />
              </span>
            )}
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
        <Shield className="h-4 w-4" />
        <span>{onboardingCopy.general.lgpd}</span>
      </div>

      <div className="text-center pt-2 border-t">
        <p className="text-sm text-muted-foreground">
          Já tem uma conta?{" "}
          <button
            type="button"
            onClick={onLoginClick}
            className="text-primary hover:underline font-medium"
          >
            Fazer login
          </button>
        </p>
        <div className="pt-2">
          <button
            type="button"
            onClick={onBackClick}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Voltar à página principal
          </button>
        </div>
      </div>
    </section>
  );
}
